/// <amd-module name="Build/Common/Library/Components/BranchLink" />
import * as React from "react";
import * as Resources from "Build/Common/Library/Resources";

import { ISourceProviderService } from 'Build/SourceProviders/SourceProvider';

import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";

import { ICancelReason } from "VSS/Core/Util/Promise";
import { format } from "VSS/Core/Util/String";
import { Icon } from "VSSUI/Icon";
import { Tooltip } from "VSSUI/TooltipEx";

//  repositoryId is used instead of repositoryName in some source providers
// requiring both here so that those who consume this control are most likely to get a valid link
export interface IBranchLinkProps {
    // default: true
    excludeTabStop?: boolean;
    repositoryType: string;
    repositoryName: string;
    repositoryId: string;
    repositoryUrl: string;
    branch: string;
    monospace: boolean;
    readonly?: boolean;
    projectNamePromise?: Promise<string | null>;
}

interface IBranchLinkState {
    branchIconName: string;
    branchName: string;
    branchLink: string;
}

export class BranchLink extends VssComponent<IBranchLinkProps, IBranchLinkState> {
    constructor(props: IBranchLinkProps, context: IVssComponentContext) {
        super(props, context);

        const sourceProviderService = context.pageContext.getService<ISourceProviderService>("ISourceProviderService");
        const sourceProvider = sourceProviderService.getSourceProvider(props.repositoryType);
        const branchName = sourceProvider ? sourceProvider.getBranchText(props.branch) : props.branch;
        const branchIconName = sourceProvider ? sourceProvider.getBranchIconName(props.branch) : "OpenSource";

        this.state = {
            branchIconName,
            branchLink: "#",
            branchName
        }
    }

    public componentDidMount() {
        super.componentDidMount();
        this._refreshStateData();
    }

    public componentDidUpdate(prevProps: IBranchLinkProps, prevState: IBranchLinkState) {
        super.componentDidUpdate(prevProps, prevState);
        if (prevProps.repositoryId !== this.props.repositoryId || prevProps.branch !== this.props.branch) {
            this._refreshStateData();
        }
    }

    public render() {
        const { excludeTabStop = true, monospace, readonly } = this.props;
        let className = "scroll-hidden flex-row flex-baseline branch-link";
        if (monospace) {
            className += " monospaced-text";
        }

        if (!readonly && this.state.branchLink) {
            return (
                <FPSLink className={className} excludeTabStop={excludeTabStop} href={this.state.branchLink} subtle={true} ariaLabel={format(Resources.BranchLabelFormat, this.state.branchName)} removeUnderline={true}>
                    <Tooltip overflowOnly={true}>
                        <span className="text-ellipsis">
                            <Icon iconName={this.state.branchIconName} className="pipelines-icon-right-margin" />
                            {this.state.branchName}
                        </span>
                    </Tooltip>
                </FPSLink>
            );
        }
        else if (this.state.branchName) {
            className += " pipelines-icon-left-margin pipeline-link";
            return (
                <div className={className} >
                    <Tooltip overflowOnly={true}>
                        <span className="text-ellipsis" aria-label={format(Resources.BranchLabelFormat, this.state.branchName)}>
                            <Icon iconName={this.state.branchIconName} className="pipelines-icon-right-margin" />
                            {this.state.branchName}
                        </span>
                    </Tooltip>
                </div>
            );
        }

        return <span></span>
    }

    private _refreshStateData() {
        const sourceProviderService = this.context.pageContext.getService<ISourceProviderService>("ISourceProviderService");
        const sourceProvider = sourceProviderService.getSourceProvider(this.props.repositoryType);
        const branchName = sourceProvider ? sourceProvider.getBranchText(this.props.branch) : this.props.branch;
        const branchIconName = sourceProvider ? sourceProvider.getBranchIconName(this.props.branch) : "OpenSource";
        if (!this.props.readonly && sourceProvider) {
            this.trackPromise(
                this.props.projectNamePromise || sourceProvider.getProjectName(this.props.repositoryId)
            ).promise.then((projectName: string | null) => 
                this.trackPromise(
                    sourceProvider.fetchBranchLink({
                        branchName: this.props.branch,
                        repositoryName: this.props.repositoryName || this.props.repositoryId,
                        repositoryUrl: this.props.repositoryUrl,
                        links: {}
                    }, projectName ? projectName : undefined)
                ).promise
            ).then(
                (branchLink: string) => {
                    this.setState({
                        branchIconName,
                        branchLink,
                        branchName
                    });
                },
                (error: ICancelReason) => {
                    if (!error.isCanceled) {
                        console.log(error);
                    }
                }
            );
        }
        else {
            this.setState({
                branchIconName,
                branchLink: "#",
                branchName
            });
        }
    }
}
